import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, LinkIcon, Copy, Check, ExternalLink, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startEvaluation, getEvaluation, generateSmartlink, getSmartlinkByRelease } from "@/lib/afmApi";
import { SectionTitle } from "@/components/common";

const scoreColor = (s) => (s >= 75 ? "#16A34A" : s >= 50 ? "#D97706" : "#DC2626");

export const ReleaseAI = ({ releaseId }) => {
  const { user } = useAuth();
  const [evalData, setEvalData] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [smart, setSmart] = useState(null);
  const [gen, setGen] = useState(false);
  const [copied, setCopied] = useState(false);
  const poll = useRef(null);

  useEffect(() => {
    getEvaluation(releaseId).then(setEvalData).catch(() => {});
    getSmartlinkByRelease(releaseId).then((d) => setSmart(d?.slug ? d : d?.smartlink?.slug ? d.smartlink : null)).catch(() => {});
    return () => clearInterval(poll.current);
  }, [releaseId]);

  useEffect(() => {
    if (evalData?.status === "analyzing" || evalData?.status === "processing") {
      poll.current = setInterval(() => {
        getEvaluation(releaseId).then((d) => {
          setEvalData(d);
          if (d.status === "done") { clearInterval(poll.current); setEvaluating(false); }
        }).catch(() => {});
      }, 3000);
      return () => clearInterval(poll.current);
    }
  }, [evalData?.status, releaseId]);

  const runEval = async () => {
    setEvaluating(true);
    try { const d = await startEvaluation(user.id, releaseId); setEvalData(d); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar el análisis"); setEvaluating(false); }
  };

  const makeLink = async () => {
    setGen(true);
    try { const d = await generateSmartlink(user.id, releaseId); const sl = d?.slug ? d : d?.smartlink; if (!sl?.slug) throw new Error("sin slug"); setSmart(sl); toast.success("Smart Link generado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el Smart Link"); }
    finally { setGen(false); }
  };

  const slugUrl = smart ? `${window.location.origin}/l/${smart.slug}` : "";
  const copy = () => { navigator.clipboard.writeText(slugUrl); setCopied(true); toast.success("Enlace copiado"); setTimeout(() => setCopied(false), 1600); };
  const busy = evalData && evalData.status !== "done";
  const done = evalData?.status === "done";

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Pitching IA */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="pitching-card">
        <SectionTitle><span className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#AC3333]" /> Análisis IA</span></SectionTitle>
        {!evalData && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-500">Evalúa tu track con IA: score de potencial, mood y playlists sugeridas.</p>
            <button data-testid="run-evaluation-button" onClick={runEval} disabled={evaluating} className="shrink-0 rounded-lg bg-[#AC3333] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#FF0505] disabled:opacity-60">Analizar</button>
          </div>
        )}
        {busy && (
          <div className="flex items-center gap-3 py-4 text-sm text-slate-500" data-testid="pitching-analyzing">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#AC3333]" /> Analizando con IA…
          </div>
        )}
        {done && (
          <div className="space-y-4" data-testid="pitching-result">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-full border-4" style={{ borderColor: scoreColor(evalData.fit_score) }}>
                <span className="font-mono text-2xl font-bold" style={{ color: scoreColor(evalData.fit_score) }}>{evalData.fit_score}</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-400">score</span>
              </div>
              <div><div className="text-sm font-semibold text-black">{evalData.verdict}</div><div className="mt-1 text-xs text-slate-500">{evalData.recommended_pitch}</div></div>
            </div>
            {evalData.mood_tags?.length > 0 && <div className="flex flex-wrap gap-2">{evalData.mood_tags.map((m) => <span key={m} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{m}</span>)}</div>}
            {evalData.playlist_fit?.length > 0 && (
              <div><div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Playlists sugeridas</div>
                <div className="space-y-1">{evalData.playlist_fit.map((p, i) => <div key={i} className="text-sm text-slate-700">• {typeof p === "string" ? p : p.name}</div>)}</div></div>
            )}
          </div>
        )}
      </div>

      {/* Smart Link */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="smartlink-card">
        <SectionTitle><span className="flex items-center gap-2"><LinkIcon className="h-5 w-5 text-[#AC3333]" /> Smart Link</span></SectionTitle>
        {!smart ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-500">Un enlace único con todas las tiendas y píxeles de seguimiento.</p>
            <button data-testid="generate-smartlink-button" onClick={makeLink} disabled={gen} className="shrink-0 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">Generar</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input readOnly value={slugUrl} data-testid="smartlink-url" className="flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 font-mono text-xs text-slate-700 outline-none" />
              <button onClick={copy} className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold hover:border-black">{copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}</button>
              <a href={`/l/${smart.slug}`} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-slate-300 px-3 py-2.5 hover:border-black"><ExternalLink className="h-4 w-4" /></a>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500"><span className="flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5" /> {typeof smart.views === "number" ? smart.views : 0} vistas · {typeof smart.clicks === "number" ? smart.clicks : (smart.clicks && typeof smart.clicks === "object" ? Object.values(smart.clicks).reduce((a, b) => a + (Number(b) || 0), 0) : 0)} clics</span></div>
          </div>
        )}
      </div>
    </div>
  );
};
